/**
 * When to keep asking provider-api for a signing URL, and for how long.
 *
 * An embedded envelope is created after the order commits, not with it: the
 * Lambda fetches the TRF, retires any prior envelopes and completes a round trip
 * to DocuSign before it records the signature row the mint needs. A clinician
 * who submits is sent straight to `/sign`, so the first mint routinely arrives
 * during that window and is refused — correctly, and only for a few seconds.
 *
 * Kept as pure functions so the policy is testable without timers or a rendered
 * page; the loop that uses them lives in `SignTrf`.
 */

/**
 * How long to keep retrying before giving up. Matches the promise the preparing
 * card makes on screen — "This usually takes under a minute" — so the page waits
 * as long as it tells the clinician it will, and no longer.
 */
export const SIGN_MINT_RETRY_BUDGET_MS = 60_000

/**
 * Gap between attempts. Short enough that a ready envelope is picked up promptly,
 * long enough that a full budget is tens of requests rather than hundreds. A
 * refusal during preparation is resolved by provider-api from its own signature
 * ledger and never reaches DocuSign, so these retries cost the vendor nothing.
 */
export const SIGN_MINT_RETRY_INTERVAL_MS = 3_000

/**
 * Whether a failed mint is worth repeating.
 *
 * Retried:
 *   - `null` — the request produced no response (offline, dropped connection).
 *   - 409 — provider-api refused. The common case here is an envelope that does
 *     not exist yet, which resolves on its own within seconds.
 *   - 502/503/504 — DocuSign or the gateway was briefly unavailable.
 *
 * Not retried: everything else, 403 and 404 above all. Those answer "you may not
 * sign this order" and "there is no such order", neither of which a later
 * attempt changes; repeating them only delays an honest error.
 *
 * Note that 409 also carries the permanent "not enabled for in-app signing"
 * refusal, which is retried too and so costs the full budget before it surfaces.
 * Separating the two means matching on the endpoint's prose; a reason code on
 * the response is the fix, and it belongs in provider-api.
 */
export function isRetryableMintStatus(status: number | null): boolean {
  if (status === null) return true
  if (status === 409) return true
  return status === 502 || status === 503 || status === 504
}

/**
 * Whether another attempt fits in the budget.
 *
 * Counts the wait as well as the attempt, so the loop stops instead of sleeping
 * past its own deadline to make a call it has already decided is too late.
 */
export function hasBudgetForAnotherAttempt(
  elapsedMs: number,
  budgetMs: number = SIGN_MINT_RETRY_BUDGET_MS,
  intervalMs: number = SIGN_MINT_RETRY_INTERVAL_MS
): boolean {
  return elapsedMs + intervalMs < budgetMs
}
